import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';

const Header = ({ companyInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const offset = 80;
          const elementPosition = element.offsetTop - offset;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.offsetTop - offset;
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
    setIsCompanyDropdownOpen(false);
  };

  const navItems = [
    { id: 'technology-demo', label: 'Tech Demo' },
    { id: 'services', label: 'Services' },
    { id: 'products', label: 'Products' },
    { id: 'portfolio', label: 'Portfolio' },
    { type: 'link', href: '/blog', label: 'Blog' },
    { id: 'contact', label: 'Contact' }
  ];

  const companyDropdownItems = [
    { id: 'about', label: 'About Us' },
    { id: 'values', label: 'Our Values' },
    { id: 'team', label: 'Our Team' },
    { type: 'link', href: '/careers', label: 'Careers' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white/80 backdrop-blur-sm shadow-sm'
      }`}
    >
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        {/* Logo */}
        <button
          onClick={() => {
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
            setIsMobileMenuOpen(false);
          }}
          className="text-3xl font-poppins font-bold text-lion-blue flex items-center gap-2 hover:text-lion-yellow transition-colors duration-300"
        >
          {companyInfo?.logo ? (
            <img 
              src={companyInfo.logo} 
              alt={`${companyInfo?.companyName || 'LionTek'} Logo`}
              className="h-10 w-auto"
              onError={(e) => {
                // Fallback to Globe icon if logo fails to load
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
          ) : null}
          <Globe 
            className={`w-8 h-8 text-lion-yellow ${companyInfo?.logo ? 'hidden' : 'block'}`} 
          />
          {companyInfo?.companyName || 'LionTek'}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 items-center text-sm font-medium">
          {/* Company Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => setIsCompanyDropdownOpen(true)}
            onMouseLeave={() => setIsCompanyDropdownOpen(false)}
          >
            <button className="text-gray-600 hover:text-lion-yellow transition-colors duration-300 py-2 flex items-center">
              Company
              <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden transition-all duration-200 ${
              isCompanyDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
            }`}>
              {companyDropdownItems.map((item, index) => (
                item.type === 'link' ? (
                  <button
                    key={index}
                    onClick={() => {
                      navigate(item.href);
                      setIsCompanyDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-lion-yellow hover:text-white transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => {
                      scrollToSection(item.id);
                      setIsCompanyDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-lion-yellow hover:text-white transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                )
              ))}
            </div>
          </div>

          {navItems.map((item, index) => (
            item.type === 'link' ? (
              <button
                key={index}
                onClick={() => {
                  navigate(item.href);
                  setIsMobileMenuOpen(false);
                }}
                className="text-gray-600 hover:text-lion-yellow transition-colors duration-300 py-2"
              >
                {item.label}
              </button>
            ) : (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-600 hover:text-lion-yellow transition-colors duration-300 py-2"
              >
                {item.label}
              </button>
            )
          ))}
        </nav>

        {/* Portal Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {/* Temporarily disabled applicant portal */}
          {/* <button 
            onClick={() => navigate('/applicant/login')}
            className="text-lion-blue font-semibold hover:text-lion-yellow transition-colors"
          >
            Applicant Portal
          </button> */}
          {/* Client Portal button hidden until portal is ready */}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-lion-blue p-2"
          aria-label="Toggle mobile menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen 
            ? 'max-h-[600px] opacity-100' 
            : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-4 bg-white/95 backdrop-blur-md border-t border-gray-200">
          {/* Company Section in Mobile */}
          <div className="border-b border-gray-100 pb-2 mb-2">
            <div className="py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Company</div>
            {companyDropdownItems.map((item, index) => (
              item.type === 'link' ? (
                <button
                  key={index}
                  onClick={() => {
                    navigate(item.href);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 pl-4 text-gray-600 hover:text-lion-yellow transition-colors duration-300"
                >
                  {item.label}
                </button>
              ) : (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left py-2 pl-4 text-gray-600 hover:text-lion-yellow transition-colors duration-300"
                >
                  {item.label}
                </button>
              )
            ))}
          </div>

          {navItems.map((item, index) => (
            item.type === 'link' ? (
              <button
                key={index}
                onClick={() => {
                  navigate(item.href);
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full text-left py-3 text-gray-600 hover:text-lion-yellow transition-colors duration-300 border-b border-gray-100 last:border-b-0"
              >
                {item.label}
              </button>
            ) : (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-3 text-gray-600 hover:text-lion-yellow transition-colors duration-300 border-b border-gray-100 last:border-b-0"
              >
                {item.label}
              </button>
            )
          ))}
          {/* Client Portal button hidden until portal is ready */}
        </div>
      </div>
    </header>
  );
};

export default Header;